import { web3ValidatorService, ValidatorWithMetadata } from './web3ValidatorService';
import { Connection, PublicKey } from '@solana/web3.js';
import { log } from '@/utils/logger';
import { configService } from '@/config';
import { apiService } from './api';

// Centralized service for all validator data - replaces database queries
export class CentralizedValidatorService {
  private connection: Connection;
  // PHASE 2: Removed in-memory caches - rely on backend RPC_CACHES + React Query
  // private validatorsCache: Map<string, ValidatorWithMetadata[]> = new Map();
  // private cacheTimestamp: Map<string, number> = new Map();
  // private readonly CACHE_DURATION = 300000;
  private currentNetwork: string = '';

  constructor(rpcUrl?: string) {
    // Use network-aware RPC URL
    const networkConfig = configService.getCurrentNetworkConfig();
    // For Connection object, always use the original RPC URL (proxy detection happens at call time)
    const effectiveRpcUrl = rpcUrl || networkConfig.rpcUrl;
    this.connection = new Connection(effectiveRpcUrl, 'confirmed');
    this.currentNetwork = networkConfig.name;

    // Listen for network changes to refresh cache
    window.addEventListener('networkChanged', this.handleNetworkChange.bind(this));
  }

  private handleNetworkChange = () => {
    const newNetworkConfig = configService.getCurrentNetworkConfig();
    if (newNetworkConfig.name !== this.currentNetwork) {
      // Update connection and clear cache (use original RPC URL for Connection object)
      this.connection = new Connection(newNetworkConfig.rpcUrl, 'confirmed');
      this.currentNetwork = newNetworkConfig.name;
      this.clearCache();

      // For HTTPS environments, force a more aggressive cache clear.
      // Targets the canonical ['validators', ...] key that all consumers
      // share (G2 consolidation — see /blocks, /validators, Dashboard,
      // Credits, NetworkStats, ProgramStake, BlockPipeline).
      if (window.location.protocol === 'https:') {
        // Clear any React Query cache for validator data
        const queryClient = (window as any).__REACT_QUERY_CLIENT__;
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: ['validators'] });
          queryClient.removeQueries({ queryKey: ['validators'] });
        }
        log.validator(`🔄 HTTPS cache invalidation for network switch to ${newNetworkConfig.name}`);
      }

      log.info(`🔄 Validator service updated to ${newNetworkConfig.name} network`, 'validator');
    }
  }

  private getRpcUrl(networkConfig: any): string {
    // For HTTP endpoints accessed through HTTPS (Cloudflare tunnel), use backend proxy
    if (networkConfig.rpcUrl.startsWith('http://') && window.location.protocol === 'https:') {
      log.info(`🔄 Using RPC proxy for ${networkConfig.name} to avoid mixed content issues`, 'validator');
      return '/api/rpc';
    }
    return networkConfig.rpcUrl;
  }

  private get shouldUseProxy(): boolean {
    const networkConfig = configService.getCurrentNetworkConfig();
    return networkConfig.rpcUrl.startsWith('http://') && window.location.protocol === 'https:';
  }

  private async makeProxiedRpcCall(method: string, params?: any[]): Promise<any> {
    const network = configService.getCurrentNetwork();
    const requestBody = { method, params: params || [], id: Math.floor(Math.random() * 1000000) };

    log.rpc(`🚀 Making proxied RPC call:`, {
      method,
      network,
      url: `/api/rpc?network=${network}`,
      requestBody,
      currentProtocol: window.location.protocol,
      currentHost: window.location.hostname,
      fullUrl: `${window.location.origin}/api/rpc?network=${network}`,
      apiBaseUrl: apiService.getBaseUrl()
    });

    const fullRpcUrl = `/api/rpc?network=${network}`;
    log.rpc(`📡 Making fetch request to: ${fullRpcUrl}`);

    const response = await fetch(fullRpcUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    log.rpc(`📥 Response received:`, {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      const errorText = await response.text();
      log.rpc(`❌ Response not OK, error text:`, errorText);
      throw new Error(`RPC call failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    log.rpc(`📄 Parsed JSON response:`, {
      hasResult: !!data.result,
      hasError: !!data.error,
      errorType: typeof data.error,
      error: data.error,
      fullErrorDetails: data.error ? JSON.stringify(data.error, null, 2) : null,
      resultType: typeof data.result,
      resultKeys: data.result ? Object.keys(data.result) : null,
      fullResponse: data
    });

    if (data.error) {
      // Handle both string and object error formats
      const errorMessage = typeof data.error === 'string' ? data.error :
                          (data.error.message || JSON.stringify(data.error));
      log.rpc(`❌ RPC Error detected:`, errorMessage);
      throw new Error(`RPC error: ${errorMessage}`);
    }

    log.rpc(`✅ Returning result for ${method}:`, data.result ? 'Success' : 'No result');
    return data.result;
  }

  private getCurrentSchema(): string {
    const networkConfig = configService.getCurrentNetworkConfig();
    return networkConfig.database?.schema || 'fortiblox_explorer_testnet';
  }

  /**
   * Get all validators with metadata from database (cached)
   */
  async getAllValidators(): Promise<ValidatorWithMetadata[]> {
    // IMPORTANT: Always get current network from configService, not from this.currentNetwork
    // This ensures we're using the right network even if the service was initialized before network was set
    const currentNetwork = configService.getCurrentNetwork();
    log.validator(`🚀 getAllValidators() called for ${currentNetwork} network (service.currentNetwork=${this.currentNetwork})`);

    try {
      // PHASE 2: Removed service-level cache - fetch directly from backend cached endpoint
      // Backend RPC_CACHES handles all caching with proper TTLs
      log.validator(`🔄 Fetching fresh data for ${currentNetwork} (backend handles caching)...`);


      const schema = this.getCurrentSchema();
      log.validator(`Network: ${currentNetwork}, Schema: ${schema}, Protocol: ${window.location.protocol}`);
      log.info(`🔄 Fetching validator data from ${schema} schema...`, 'validator');

      // Wave F (WP #3619): replaced raw-SQL apiService.query against
      // fortiblox_validator_location with the typed /api/validators/locations
      // endpoint. The server owns the SELECT projection.
      log.validator(`🗃️ Fetching locations via typed endpoint on schema: ${schema}`);
      log.validator(`🔧 API Service Base URL: ${apiService.getBaseUrl()}`);
      log.validator(`🔍 Network context: currentNetwork=${currentNetwork}, this.currentNetwork=${this.currentNetwork}`);

      const locationsUrl = apiService.buildUrl(`/api/validators/locations?network=${currentNetwork}`);
      const locationHttp = await fetch(locationsUrl);
      const locationOk = locationHttp.ok;
      const locationBody = locationOk ? await locationHttp.json() : null;
      const locations = locationBody?.locations || [];

      log.validator(`📊 Location query result:`, {
        success: locationOk,
        dataLength: locations.length,
        status: locationHttp.status,
        hasData: !!locationBody
      });

      log.validator(`🗃️ Location query response:`, {
        hasData: !!locationBody,
        dataLength: locations.length,
        responseKeys: locationBody ? Object.keys(locationBody) : [],
        url: locationsUrl
      });

      log.validator(`📍 Processed locations count: ${locations.length}`);

      if (locations.length > 0) {
        log.validator(`First 3 locations:`, locations.slice(0, 3));
      } else {
        log.warn(`[VALIDATOR DEBUG] ❌ NO LOCATIONS FOUND in ${schema}! Full response:`, locationBody);
      }

      log.info(`📍 Found ${locations.length} validators with locations in ${schema}`, 'validator');

      // Get RPC validator data for current network using cached endpoint
      log.validator(`🌐 Fetching RPC data from cache for ${currentNetwork}`);
      log.info(`✅ Using cached vote-accounts endpoint for ${currentNetwork}`, 'validator');

      // Fetch vote accounts, cluster nodes, AND the enriched validator list.
      // Lane V.2 (WP #4084): /api/validators/list carries the `nameProvenance`
      // field + the canonical name (config or proxy) — we MUST merge it so the
      // UI can render the verified / inferred / unverified badges. Failure to
      // load it falls back to the legacy RPC-only path with provenance=null
      // (which formatValidatorLabel() treats as `unverified`).
      const [voteResponse, clusterResponse, enrichedListResponse] = await Promise.all([
        fetch(`/api/rpc/vote-accounts?network=${currentNetwork}`, { cache: 'no-store' }),
        fetch(`/api/rpc/cluster-nodes?network=${currentNetwork}`, { cache: 'no-store' }),
        fetch(`/api/validators/list?network=${currentNetwork}`).catch(() => null)
      ]);

      if (!voteResponse.ok) {
        throw new Error(`Vote accounts HTTP ${voteResponse.status}: ${voteResponse.statusText}`);
      }
      if (!clusterResponse.ok) {
        log.warn(`Cluster nodes HTTP ${clusterResponse.status}: ${clusterResponse.statusText} - continuing without versions`);
      }

      const voteAccounts = await voteResponse.json();
      const clusterNodesData = clusterResponse.ok ? await clusterResponse.json() : { nodes: [] };
      // Backend returns { nodes: [...], cacheAge: ... }
      const clusterNodes = clusterNodesData.nodes || clusterNodesData || [];

      // Lane V.2: build pubkey → { name, nameProvenance } map from the
      // enriched list endpoint. Tolerates failure — if /api/validators/list
      // is unreachable, we render unverified (safe default).
      const enrichedNameMap = new Map<string, { name: string | null; nameProvenance: 'config' | 'keybase' | 'proxy' | 'fallback' | null }>();
      if (enrichedListResponse && enrichedListResponse.ok) {
        try {
          const enrichedBody = await enrichedListResponse.json();
          const enrichedRows = Array.isArray(enrichedBody?.validators) ? enrichedBody.validators : [];
          enrichedRows.forEach((row: any) => {
            const pk = row.pubkey || row.nodePubkey;
            if (!pk) return;
            const prov = row.nameProvenance;
            const provenance = prov === 'config' || prov === 'keybase' || prov === 'proxy' || prov === 'fallback' ? prov : null;
            enrichedNameMap.set(pk, {
              name: typeof row.name === 'string' && row.name.length > 0 ? row.name : null,
              nameProvenance: provenance,
            });
          });
          log.validator(`🏷️  Lane V.2: loaded nameProvenance for ${enrichedNameMap.size} validators`);
        } catch (e) {
          log.warn(`Lane V.2: failed to parse /api/validators/list — provenance will default to unverified`);
        }
      } else {
        log.warn(`Lane V.2: /api/validators/list unavailable — provenance will default to unverified`);
      }

      // Build cluster node map for quick version lookups
      const clusterNodeMap = new Map((Array.isArray(clusterNodes) ? clusterNodes : []).map((node: any) => [node.pubkey, node]));
      log.validator(`📊 Cluster nodes map has ${clusterNodeMap.size} entries`);

      const allValidators = [...voteAccounts.current, ...voteAccounts.delinquent];
      log.validator(`📊 RPC returned ${allValidators.length} validators (${voteAccounts.current.length} current, ${voteAccounts.delinquent.length} delinquent)`);

      if (allValidators.length > 0) {
        log.validator(`First 3 RPC validators:`, allValidators.slice(0, 3).map(v => ({
          nodePubkey: v.nodePubkey?.slice(0, 8) + '...',
          votePubkey: v.votePubkey?.slice(0, 8) + '...',
          activatedStake: v.activatedStake
        })));
      }

      log.info(`📊 Found ${allValidators.length} total validators from RPC (${voteAccounts.current.length} current, ${voteAccounts.delinquent.length} delinquent)`, 'validator');

      // Fetch validator metadata from blockchain Config accounts
      log.validator(`🔍 Fetching validator metadata from blockchain Config accounts...`);

      // Log sample nodePubkeys being sent for debugging
      const nodePubkeys = allValidators.map(v => v.nodePubkey);
      log.validator(`📝 Sample nodePubkeys being sent (first 3):`, nodePubkeys.slice(0, 3));

      const validatorMetadata = await web3ValidatorService.batchFindValidatorMetadata(nodePubkeys);
      log.validator(`✅ Retrieved metadata for ${validatorMetadata.size} validators`);

      // Create a map of locations by validator identity
      const locationMap = new Map<string, any>();
      log.validator(`🔗 Building location map from ${locations.length} locations...`);

      locations.forEach(loc => {
        if (loc.validator_identity) {
          locationMap.set(loc.validator_identity, loc);
        }
        if (loc.vote_account) {
          locationMap.set(loc.vote_account, loc);
        }
      });

      log.validator(`📍 Location map has ${locationMap.size} entries`);

      if (locations.length > 0) {
        log.validator(`Sample location map entries:`, Array.from(locationMap.entries()).slice(0, 3));
      }

      // Merge RPC data with database locations
      log.validator(`🔗 Starting merge process...`);
      let matchCount = 0;
      const mergedValidators: ValidatorWithMetadata[] = allValidators.map((v, index) => {
        const location = locationMap.get(v.nodePubkey) || locationMap.get(v.votePubkey);
        const metadata = validatorMetadata.get(v.nodePubkey);

        if (location) {
          matchCount++;
          if (matchCount <= 5) { // Only log first 5 matches to avoid spam
            log.validator(`✅ Match ${matchCount}: ${v.nodePubkey.slice(0, 8)}... -> ${location.city_name}, ${location.country_name}`);
          }
          log.info(`✅ Matched validator ${v.nodePubkey.slice(0, 8)}... with location: ${location.city_name}, ${location.country_name}`, 'validator');
        } else if (index < 5) { // Only log first 5 non-matches to avoid spam
          log.validator(`❌ No location found for validator ${v.nodePubkey.slice(0, 8)}... (vote: ${v.votePubkey.slice(0, 8)}...)`);
        }

        // Lane V.2 (WP #4084): merge enriched name + provenance. Priority:
        //   1. On-chain Config metadata (highest trust, provenance=config)
        //   2. Enriched-list payload (may carry proxy name + provenance=proxy)
        //   3. Location DB legacy `validator_name`
        //   4. null → render unverified (truncated pubkey)
        // The legacy "Validator <prefix>..." synthesizer is GONE — leaving the
        // name null lets the UI render the unverified state instead of
        // manufacturing data we don't have.
        const enriched = enrichedNameMap.get(v.nodePubkey) || null;
        const resolvedName =
          metadata?.name ||
          enriched?.name ||
          location?.validator_name ||
          null;
        const resolvedProvenance: 'config' | 'keybase' | 'proxy' | 'fallback' | null =
          metadata?.name ? 'config' :
          enriched?.nameProvenance ? enriched.nameProvenance :
          null;

        return {
          pubkey: v.nodePubkey,
          nodePubkey: v.nodePubkey,
          votePubkey: v.votePubkey,
          name: resolvedName,
          nameProvenance: resolvedProvenance,
          status: voteAccounts.current.includes(v) ? 'active' : 'delinquent' as const,
          stake: v.activatedStake || 0,
          activatedStake: v.activatedStake || 0,
          commission: v.commission || 0,
          lastVote: v.lastVote || 0,
          rootSlot: v.rootSlot || 0,
          epochVoteAccount: voteAccounts.current.includes(v),
          epochCredits: v.epochCredits || [],
          credits: v.epochCredits && v.epochCredits.length > 0 ? v.epochCredits[v.epochCredits.length - 1][1] : 0,
          version: (clusterNodeMap.get(v.nodePubkey) as any)?.version || undefined,
          uptime: undefined,
          iconUrl: metadata?.icon_url,
          icon: metadata?.icon_url,
          website: metadata?.website,
          details: metadata?.description,
          location: location ? {
            country: location.country_name,
            countryCode: location.metadata?.country?.isoAlpha2 || undefined,
            city: location.city_name,
            latitude: parseFloat(location.latitude),
            longitude: parseFloat(location.longitude)
          } : undefined
        };
      });

      // PHASE 2: Cache removed - backend RPC_CACHES + React Query handle all caching
      // this.validatorsCache.set(currentNetwork, mergedValidators);
      // this.cacheTimestamp.set(currentNetwork, Date.now());
      log.validator(`✅ Returning ${mergedValidators.length} validators for ${currentNetwork} (cached by backend)`);

      const validatorsWithLocations = mergedValidators.filter(v => v.location);
      log.validator(`Final result: ${mergedValidators.length} total validators, ${validatorsWithLocations.length} with locations`);

      // Production debug - show in page title (disabled to use proper site title)
      // if (typeof window !== 'undefined') {
      //   document.title = `${this.currentNetwork}: ${validatorsWithLocations.length}/${mergedValidators.length} validators with locations`;
      // }

      if (validatorsWithLocations.length > 0) {
        log.validator(`Validators with locations:`, validatorsWithLocations.map(v => ({
          id: v.nodePubkey.slice(0, 8),
          city: v.location?.city,
          country: v.location?.country,
          lat: v.location?.latitude,
          lng: v.location?.longitude
        })));
      } else {
        log.warn(`[VALIDATOR DEBUG] ❌ NO VALIDATORS WITH LOCATIONS for ${this.currentNetwork}`);
      }

      log.info(`✅ Retrieved ${mergedValidators.length} validators total, ${validatorsWithLocations.length} with locations`, 'validator');

      if (validatorsWithLocations.length > 0) {
        log.info(`🗺️ Validators with locations: ${validatorsWithLocations.map(v => `${v.nodePubkey.slice(0, 8)}(${v.location?.city})`).join(', ')}`, 'validator');
      } else {
        log.warn(`❌ No validators found with location data for ${this.currentNetwork}`, 'validator');
      }

      return mergedValidators;

    } catch (error) {
      log.error(`[VALIDATOR DEBUG] ❌ Error in getAllValidators:`, error);
      log.error(`❌ Failed to fetch validators: ${error.message}`, 'validator');

      // Fallback to basic RPC data without locations using cached endpoint
      try {
        log.info(`🔄 Fallback: Using cached endpoints (vote-accounts + cluster-nodes)`, 'validator');
        const currentNetwork = configService.getCurrentNetwork();

        // Fetch both vote accounts and cluster nodes for version data
        const [voteResponse, clusterResponse] = await Promise.all([
          fetch(`/api/rpc/vote-accounts?network=${currentNetwork}`),
          fetch(`/api/rpc/cluster-nodes?network=${currentNetwork}`)
        ]);

        if (!voteResponse.ok) {
          throw new Error(`HTTP ${voteResponse.status}: ${voteResponse.statusText}`);
        }
        // Continue even if cluster nodes fail
        if (!clusterResponse.ok) {
          log.warn(`Fallback: Cluster nodes unavailable, continuing without versions`);
        }

        const voteAccounts = await voteResponse.json();
        const clusterNodesData = clusterResponse.ok ? await clusterResponse.json() : { nodes: [] };
        // Backend returns { nodes: [...], cacheAge: ... }
        const clusterNodes = clusterNodesData.nodes || clusterNodesData || [];

        // Build cluster node map for version lookups
        const clusterNodeMap = new Map((Array.isArray(clusterNodes) ? clusterNodes : []).map((node: any) => [node.pubkey, node]));

        const allValidators = [...voteAccounts.current, ...voteAccounts.delinquent];

        const basicValidators: ValidatorWithMetadata[] = allValidators.map(v => ({
          pubkey: v.nodePubkey,
          nodePubkey: v.nodePubkey,
          votePubkey: v.votePubkey,
          // Lane V.2 (WP #4084): no synthesized name — null lets the UI render
          // the unverified state (truncated pubkey + badge).
          name: null,
          nameProvenance: null,
          status: voteAccounts.current.includes(v) ? 'active' : 'delinquent' as const,
          stake: v.activatedStake || 0,
          activatedStake: v.activatedStake || 0,
          commission: v.commission || 0,
          lastVote: v.lastVote || 0,
          rootSlot: v.rootSlot || 0,
          epochVoteAccount: voteAccounts.current.includes(v),
          epochCredits: v.epochCredits || [],
          credits: v.epochCredits && v.epochCredits.length > 0 ? v.epochCredits[v.epochCredits.length - 1][1] : 0,
          version: (clusterNodeMap.get(v.nodePubkey) as any)?.version || undefined,
          uptime: undefined,
          iconUrl: undefined,
          icon: undefined,
          website: undefined,
          details: undefined,
          location: undefined
        }));

        return basicValidators;
      } catch (fallbackError) {
        log.error(`❌ Fallback RPC call also failed: ${fallbackError.message}`, 'validator');
        return [];
      }
    }
  }

  /**
   * Get a specific validator by address
   */
  async getValidatorByAddress(address: string): Promise<ValidatorWithMetadata | null> {
    const allValidators = await this.getAllValidators();

    // Try to find by nodePubkey first, then by pubkey (they should be the same)
    const validator = allValidators.find(v =>
      v.nodePubkey === address ||
      v.pubkey === address ||
      v.votePubkey === address
    );

    return validator || null;
  }

  /**
   * Get validators with additional RPC data for detailed view
   */
  async getValidatorWithRpcData(address: string): Promise<{
    validator: ValidatorWithMetadata;
    rpcData: any;
    performance: any;
  } | null> {
    const validator = await this.getValidatorByAddress(address);

    if (!validator) {
      return null;
    }

    try {
      // Get additional RPC data from cached endpoints
      const currentNetwork = configService.getCurrentNetwork();
      log.info(`✅ Fetching validator details from cached endpoints for ${currentNetwork}`, 'validator');

      const [voteResponse, clusterResponse] = await Promise.all([
        fetch(`/api/rpc/vote-accounts?network=${currentNetwork}`),
        fetch(`/api/rpc/cluster-nodes?network=${currentNetwork}`)
      ]);

      if (!voteResponse.ok || !clusterResponse.ok) {
        throw new Error(`HTTP error: vote=${voteResponse.status}, cluster=${clusterResponse.status}`);
      }

      const [voteAccounts, clusterNodesData] = await Promise.all([
        voteResponse.json(),
        clusterResponse.json()
      ]);
      // Backend returns { nodes: [...], cacheAge: ... }
      const clusterNodes = clusterNodesData.nodes || clusterNodesData || [];

      // Find this validator in RPC data
      const allValidators = [...voteAccounts.current, ...voteAccounts.delinquent];
      const rpcValidator = allValidators.find(v => v.nodePubkey === address);

      // Find gossip node info
      const gossipNode = (Array.isArray(clusterNodes) ? clusterNodes : []).find(n => n.pubkey === address);

      // Calculate performance metrics
      const performance = this.calculatePerformance(rpcValidator);

      return {
        validator,
        rpcData: {
          ...rpcValidator,
          gossip: gossipNode
        },
        performance
      };

    } catch (error) {
      log.info('Error fetching additional RPC data: ' + error, 'validator');
      return {
        validator,
        rpcData: null,
        performance: null
      };
    }
  }

  /**
   * Calculate validator performance metrics
   */
  private calculatePerformance(rpcValidator: any) {
    if (!rpcValidator) return null;

    const currentEpochCredits = rpcValidator.epochCredits && rpcValidator.epochCredits.length > 0
      ? rpcValidator.epochCredits[rpcValidator.epochCredits.length - 1][1]
      : 0;

    // Calculate skip rate (simplified)
    const skipRate = 0; // Would need historical data for accurate calculation

    // Calculate APR (simplified - would need more complex calculation)
    const estimatedAPR = 5.2; // Placeholder

    return {
      currentEpochCredits,
      skipRate,
      estimatedAPR,
      isActive: rpcValidator.activatedStake > 0,
      lastVote: rpcValidator.lastVote || 0,
      rootSlot: rpcValidator.rootSlot || 0
    };
  }

  /**
   * Clear cache to force fresh data (optionally for specific network)
   * PHASE 2: Service-level cache removed - this method now just clears web3 service cache
   * React Query invalidation handles frontend cache clearing
   */
  clearCache(network?: string): void {
    // PHASE 2: No local cache to clear anymore
    log.info(`🔄 Cache clear requested for ${network || 'ALL networks'} - handled by React Query`, 'validator');
    // Also clear the underlying web3ValidatorService cache
    web3ValidatorService.clearCache();
  }

  /**
   * Get validator statistics
   */
  async getValidatorStats() {
    const validators = await this.getAllValidators();

    const totalValidators = validators.length;
    const activeValidators = validators.filter(v => v.stake && v.stake > 0).length;
    const totalStake = validators.reduce((sum, v) => sum + (v.stake || 0), 0);
    const averageCommission = validators.reduce((sum, v) => sum + (v.commission || 0), 0) / totalValidators;

    return {
      totalValidators,
      activeValidators,
      totalStake,
      averageCommission: Math.round(averageCommission * 100) / 100,
      withMetadata: validators.filter(v => v.name && !v.name.includes('...')).length,
      withIcons: validators.filter(v => v.iconUrl).length
    };
  }
}

// Export singleton instance
export const centralizedValidatorService = new CentralizedValidatorService();
export default centralizedValidatorService;